import {
    DoneAll,
    PlayArrow,
    CalendarMonth,
    Replay,
    Close,
    People,
    CalendarToday,
    RadioButtonUnchecked,
    Timer
} from "@mui/icons-material";
import { Box, Paper, Typography, Chip, Tooltip } from "@mui/material";
import { EPeriodicStatus } from "../../models/periodic/periodic";
import { ITaskHistory } from "../../models/periodic/taskHistory";
import { Fragment, ReactNode } from "react";
import { AccessTime, History, Cancel, CameraAlt } from "@mui/icons-material";
import { TFunction } from "i18next";
import moment from "moment";
import { TOVERIFY_COLOR, OVERDUE_COLOR, DEFAULT, ACCEPTED_COLOR, REFUSED_COLOR } from "../../utils/periodicUtils";

export const getEffectiveStatus = (status: EPeriodicStatus, taskDate: string | Date): EPeriodicStatus => {
    if (status === EPeriodicStatus.Todo && moment(taskDate).isBefore(moment(), 'day')) {
        return EPeriodicStatus.Overdue;
    }
    return status;
};

export const getStatusConfig = (status: EPeriodicStatus, t: TFunction) => {

    switch (status) {
        case EPeriodicStatus.Accepted:
            return { color: ACCEPTED_COLOR, bg: "#e8f5e9", icon: <DoneAll sx={{ fontSize: 22 }} />, label: t("status.Accepted"), labelSub: t("periodical:timeline.accepted") };
        case EPeriodicStatus.Refused:
            return { color: REFUSED_COLOR, bg: "#ffebee", icon: <Close sx={{ fontSize: 22 }} />, label: t("status.Refused"), labelSub: t("periodical:timeline.refused") };
        case EPeriodicStatus.ToVerify:
            return { color: TOVERIFY_COLOR, bg: `#0288d115`, icon: <Replay sx={{ fontSize: 22 }} />, label: t("status.ToVerify"), labelSub: t("periodical:timeline.toVerify") };
        case EPeriodicStatus.Overdue:
            return { color: OVERDUE_COLOR, bg: "#fff4e5", icon: <RadioButtonUnchecked sx={{ fontSize: 22 }} />, label: t("status.Overdue"), labelSub: "" };
        default:
            return { color: DEFAULT, bg: "#f5f5f5", icon: < CalendarMonth />, label: t("status.Todo"), labelSub: "" };
    }
};

interface StatBoxProps {
    label: string;
    value: string | number;
    icon: ReactNode;
    color: string;
    topLabel: string;
}

const StatBox = ({ label, value, icon, color, topLabel }: StatBoxProps) => (
    <Paper
        elevation={0}
        sx={{
            p: 2.5,
            flex: 1,
            minWidth: 0,
            border: '1px solid #f0f0f0', borderRadius: 4,
            position: 'relative',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }
        }}
    >
        <Typography
            variant="caption"
            sx={{
                position: 'absolute',
                top: 20,
                right: 20,
                fontWeight: 900,
                color: '#bdc3c7',
                textTransform: 'uppercase',
                letterSpacing: 1
            }}
        >
            {topLabel}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: `${color}15`,
                color: color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1
            }}>
                {icon}
            </Box>
            <Box>
                <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1, color: '#2c3e50', fontSize: 15 }}>
                    {value}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mt: 1, fontSize: 12 }}>
                    {label}
                </Typography>
            </Box>
        </Box>
    </Paper>
);

interface TaskStatsOverviewProps {
    history: ITaskHistory[];
    t: TFunction;
}
export const TaskStatsOverview = ({ history, t }: TaskStatsOverviewProps) => {
    const totalActions = history.length;
    const rejections = history.filter(h => h.status === EPeriodicStatus.Refused).length;
    const totalPhotos = history.reduce((acc, curr) => acc + (curr.pictureUrls?.length || 0), 0);
    const lastActionDate = history.length > 0 ? history[0].actionDate : null;

    const timeAgo = lastActionDate
        ? moment.utc(lastActionDate).local().fromNow(true)
        : t("periodical:validation.none");


    const getProcessingTime = () => {
        const finalAction = history.find(h =>
            h.status === EPeriodicStatus.Accepted ||
            h.status === EPeriodicStatus.Refused
        );

        const submissionAction = history.find(h => h.status === EPeriodicStatus.ToVerify);

        if (finalAction && submissionAction) {
            const start = moment(submissionAction.actionDate);
            const end = moment(finalAction.actionDate);
            return end.from(start, true);
        }

        return t("periodical:pending");
    };

    const processingTime = getProcessingTime();

    return (
        <Box sx={{
            display: 'grid',
            gap: 2,
            mb: 4,
            gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(5, 1fr)'
            }
        }}>
            <StatBox
                topLabel={t("periodical:stats.topLabels.total")}
                label={t("periodical:stats.totalActions")}
                value={totalActions}
                icon={<History fontSize="small" />}
                color="#34495e"
            />

            <Tooltip title={t("periodical:stats.recency")} arrow placement="top">
                <Box sx={{ flex: 1 }}>
                    <StatBox
                        topLabel={t("periodical:stats.topLabels.recency")}
                        label={t("validation.lastAction")}
                        value={timeAgo}
                        icon={<AccessTime fontSize="small" />}
                        color={ACCEPTED_COLOR}
                    />
                </Box>
            </Tooltip>

            <StatBox
                topLabel={t("periodical:stats.topLabels.issues")}
                label={t("periodical:stats.issues")}
                value={rejections}
                icon={<Cancel fontSize="small" />}
                color={REFUSED_COLOR}
            />

            <Tooltip title={t("periodical:stats.efficiency")} arrow placement="top">
                <Box sx={{ flex: 1 }}>
                    <StatBox
                        topLabel={t("periodical:stats.topLabels.efficiency")}
                        label={t("periodical:stats.processingTime")}
                        value={processingTime}
                        icon={<Timer fontSize="small" />}
                        color="#16a085"
                    />
                </Box>
            </Tooltip>

            <StatBox
                topLabel={t("periodical:stats.topLabels.media")}
                label={t("periodical:stats.media")}
                value={totalPhotos}
                icon={<CameraAlt fontSize="small" />}
                color="#9b59b6"
            />

        </Box>
    );
};

export const TaskTimeline = ({ history, startDate, t }: { history: ITaskHistory[], startDate: string, t: TFunction }) => {
    if (!history || history.length === 0) return null;
    const people = Array.from(new Set(history.flatMap(h => [h.userName, h.managerName]).filter(Boolean)));
    const sortedEvents = [...history].sort((a, b) => new Date(a.actionDate).getTime() - new Date(b.actionDate).getTime());
    const lastActionDate = moment(sortedEvents[sortedEvents.length - 1].actionDate);
    const start = moment(startDate);

    const diffDays = lastActionDate.diff(start, 'days');

    const durationMessage =
        diffDays < 0
            ? t("info.inAdvance", { count: Math.abs(diffDays) })
            : `${diffDays} ${t("common:header.RecurringDates.days")}`;


    const getTimelineStyle = (status: EPeriodicStatus) => {
        switch (status) {
            case EPeriodicStatus.ToVerify:
                return { color: TOVERIFY_COLOR, bg: '#eff6ff', icon: <Replay sx={{ fontSize: 22 }} /> };
            case EPeriodicStatus.Refused:
                return { color: REFUSED_COLOR, bg: '#fee2e2', icon: <Close sx={{ fontSize: 22 }} /> };
            case EPeriodicStatus.Accepted:
                return { color: ACCEPTED_COLOR, bg: "#e8f5e9", icon: <DoneAll sx={{ fontSize: 22 }} /> };
            case EPeriodicStatus.Overdue:
                return { color: OVERDUE_COLOR, bg: "#e3ae2b", icon: <DoneAll sx={{ fontSize: 22 }} /> };

            default:
                return { color: DEFAULT, bg: '#f0fdf4', icon: <CalendarMonth sx={{ fontSize: 22 }} /> };
        }
    };

    return (
        <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 4, border: '1px solid #f0f0f0' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 5 }}>
                <Box sx={{ bgcolor: '#eef2ff', color: '#6366f1', p: 0.8, borderRadius: 1.5, display: 'flex' }}>
                    <PlayArrow sx={{ fontSize: 18, transform: 'rotate(-90deg) scaleY(-1)' }} />
                </Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#111827' }}>
                    {t("timeline.title")}
                </Typography>
            </Box>

            {/* Timeline Row */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', px: 1, mb: 2 }}>

                {/* START NODE */}
                <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 5, minWidth: 80 }}>
                    <Box sx={{
                        width: 52, height: 52, borderRadius: '50%', bgcolor: '#f0fdf4', color: '#22c55e',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1
                    }}>
                        <PlayArrow sx={{ fontSize: 26 }} />
                    </Box>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#111827', display: 'block' }}>
                        {t("timeline.started")}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {moment(startDate).local().format("LL")}
                    </Typography>
                </Box>

                {sortedEvents.map((ev, i) => {
                    const style = getTimelineStyle(ev.status);
                    const prevColor = i === 0 ? '#22c55e' : getTimelineStyle(sortedEvents[i - 1].status).color;

                    return (
                        <Fragment key={i}>
                            <Box sx={{
                                flex: 1, height: 4, mt: 3, mx: -2,
                                background: `linear-gradient(90deg, ${prevColor} 0%, ${style.color} 100%)`,
                                opacity: 0.5
                            }} />

                            <Box sx={{ textAlign: 'center', position: 'relative', zIndex: i + 6, minWidth: 100 }}>
                                <Box sx={{
                                    width: 52, height: 52, borderRadius: '50%',
                                    bgcolor: style.bg, color: style.color,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    border: '4px solid #fff',
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                                    mx: 'auto', mb: 1
                                }}>
                                    {style.icon}
                                </Box>
                                <Typography variant="caption" sx={{ fontWeight: 800, color: '#111827', display: 'block' }}>
                                    {getStatusConfig(ev.status, t).labelSub}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                    {moment(ev.actionDate).local().format("LL")}
                                </Typography>
                            </Box>
                        </Fragment>
                    );
                })}
            </Box>

            <Box sx={{
                mt: 4,
                pt: 2,
                borderTop: '1px dashed #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: '#f3f4f6',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1.5
                }}>
                    <CalendarToday sx={{ fontSize: 16, color: '#4b5563' }} />
                    <Typography variant="caption" sx={{ color: '#4b5563', fontWeight: 700 }}>
                        {t("timeline.duration")}: {durationMessage}
                    </Typography>


                </Box>

                <Tooltip
                    title={
                        <Box sx={{ p: 0.5 }}>
                            {people.map(name => (
                                <Typography key={name as string} variant="caption" display="block" sx={{ fontWeight: 600 }}>
                                    • {name as string}
                                </Typography>
                            ))}
                        </Box>
                    }
                    arrow
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: '#f3f4f6',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1.5
                    }}>
                        <People sx={{ fontSize: 16, color: '#4b5563' }} />
                        <Typography variant="caption" sx={{ color: '#4b5563', fontWeight: 700 }}>
                            {t("timeline.people")}: {people.length}
                        </Typography>
                    </Box>
                </Tooltip>
            </Box>
        </Paper>
    );
};